import { useEffect, useRef } from 'react';
import type { Card, Seat } from '../games/heads-up-poker';
import { useRoom } from '../state/room';
import { pokerProvidePositions, revealForPositions } from './mps-context';
import { type Role, useMentalPokerHand } from './useMentalPokerHand';

export interface MentalPokerDriverProps {
  board: number;
  hand: number;
  role: Role | null;
  mySeat: Seat | null;
  awaiting: string | null;
  handPhase: string | null;
  matchCode: string;
  engineMyHole: [Card, Card] | null;
  engineBoard: Card[];
  active: boolean;
  myTurnAllowed: boolean;
  toAct: Seat | null;
  boardLen: number;
}

/** Submit only cards reconstructed in the current context, accompanied by their own private proof. */
export function MentalPokerDriver(props: MentalPokerDriverProps): null {
  useMentalPokerHand(props);
  const results = useRoom(state => state.mps.get(`${props.board}:${props.hand}`) ?? null);
  const context = results?.context;
  const latest = useRef(props);
  latest.current = props;
  const revealedLength = results?.revealedBoard.length ?? 0;
  const hasHoleReveal = !!results?.holeReveal;
  // biome-ignore lint/correctness/useExhaustiveDependencies: private proof readiness intentionally resumes submission
  useEffect(() => {
    if (!props.active || !props.myTurnAllowed || !props.awaiting || !context || !props.role || props.role === 'spectator') return;
    const provide = () => {
      const currentProps = latest.current;
      const state = useRoom.getState();
      const current = state.mps.get(`${props.board}:${props.hand}`);
      if (!current || current.context !== context || current.cheatDetected || current.cryptoError || state.code !== props.matchCode || state.you !== props.role || currentProps.board !== props.board || currentProps.hand !== props.hand || currentProps.awaiting !== props.awaiting || !currentProps.active || !currentProps.myTurnAllowed || currentProps.boardLen !== props.boardLen || currentProps.role !== props.role || currentProps.mySeat !== props.mySeat || currentProps.toAct !== props.toAct) return;
      if (props.awaiting === 'holes') {
        if (!props.mySeat || props.toAct !== props.mySeat || !current.myHole || current.holeReveal?.context !== context) return;
        state.sendMove(props.board, {provide_my_hole: {seat: props.mySeat, cards: current.myHole, reveal: current.holeReveal}});
      } else {
        const positions = pokerProvidePositions(props.awaiting, props.boardLen);
        const reveal = revealForPositions(context, current.boardRevealByIndex, positions);
        if (!reveal) return;
        const cards = positions.map(position => current.revealedBoard[position - 4]);
        if (cards.some(card => !card)) return;
        state.sendMove(props.board, {provide_board_cards: {cards, reveal}});
      }
    };
    const timer = setTimeout(provide, 150);
    const retry = setInterval(provide, 2500);
    return () => { clearTimeout(timer); clearInterval(retry); };
  }, [props.active, props.myTurnAllowed, props.awaiting, props.role, props.mySeat, props.toAct, props.board, props.hand, props.boardLen, props.matchCode, context, revealedLength, hasHoleReveal]);
  return null;
}
